import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/FirebaseAuthContext';
import Logo from '../../components/ui/Logo';

const MIN_BOOKS = 1;
const MAX_BOOKS = 100;
const MARKS = [6, 12, 24, 52];

const ReadingGoals: React.FC = () => {
  const navigate = useNavigate();
  const { updateUserPreferences } = useAuth();
  const [error, setError] = useState('');
  const [booksPerYear, setBooksPerYear] = useState(12);

  const handleBooksPerYearChange = (increment: boolean) => {
    setBooksPerYear(prev => {
      const newValue = increment ? prev + 1 : prev - 1;
      return Math.max(MIN_BOOKS, Math.min(newValue, MAX_BOOKS));
    });
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBooksPerYear(Number(e.target.value));
  };

  const getDaysLeftInYear = () => {
    const today = new Date();
    const endOfYear = new Date(today.getFullYear(), 11, 31);
    const diffTime = endOfYear.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleComplete = async () => {
    try {
      await updateUserPreferences({
        goals: {
          booksPerYear,
          readingTimePerDay: 30,
          readingDaysPerWeek: 5
        },
        hasCompletedOnboarding: true
      });
      navigate('/onboarding/complete');
    } catch (error) {
      setError('Failed to save preferences. Please try again.');
    }
  };

  const handleBack = () => {
    navigate('/onboarding/genres');
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#fcfaf3]">
      {/* Sidebar */}
      <div className="hidden md:flex flex-col justify-between w-full max-w-[400px] bg-[#2d2d2d] text-white py-10 px-8 min-h-screen">
        <div>
          <div className="flex items-center mb-2">
            <Logo variant="light" />
          </div>
          <div className="mt-12">
            <h2 className="text-2xl font-semibold mb-2">Welcome to Smata</h2>
            <p className="text-base text-gray-300 mb-10">Let's set up your reading experience in just a few steps</p>
            <div className="flex flex-col items-start gap-0 mt-8">
              <div className="flex items-center gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-gray-400 text-gray-400 font-bold opacity-60">1</div>
                  <div className="border-l-2 border-dotted border-gray-400" style={{ height: '36px' }}></div>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-white bg-white text-[#2d2d2d] font-bold">2</div>
                </div>
                <div className="flex flex-col justify-between h-[88px] py-1">
                  <span className="text-base opacity-60">Tell us about your reading preferences</span>
                  <span className="text-base font-semibold text-white">Set your reading goals</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div></div>
      </div>
      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-start items-center px-4 sm:px-8 py-12">
        <div className="w-full max-w-2xl">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 mb-8"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Set your reading goals</h1>
          <p className="mb-8 text-gray-600">Setting goals helps you build a consistent reading habit</p>
          <div className="bg-white p-8 rounded-xl border border-gray-200 mb-8">
            <div className="text-base font-medium text-gray-900 mb-2">
              How many books would you like to read this year? <span className="font-semibold">({getDaysLeftInYear()} days left)</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-6xl font-bold text-[#ff6633] mb-2">{booksPerYear}</div>
              <div className="text-base text-gray-700 mb-4">
                Books per year (about {((booksPerYear / (getDaysLeftInYear() / 30.44))).toFixed(1)} per month)
              </div>
              <div className="flex items-center gap-6 mb-4">
                <button
                  onClick={() => handleBooksPerYearChange(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 text-2xl hover:bg-gray-50 focus:outline-none"
                  aria-label="Decrease books"
                  disabled={booksPerYear <= MIN_BOOKS}
                >
                  -
                </button>
                <input
                  type="range"
                  min={MIN_BOOKS}
                  max={MAX_BOOKS}
                  value={booksPerYear}
                  onChange={handleSliderChange}
                  className="w-56 h-2 accent-[#ff6633] bg-gray-200 rounded-full focus:outline-none"
                  style={{ accentColor: '#ff6633' }}
                />
                <button
                  onClick={() => handleBooksPerYearChange(true)}
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 text-2xl hover:bg-gray-50 focus:outline-none"
                  aria-label="Increase books"
                  disabled={booksPerYear >= MAX_BOOKS}
                >
                  +
                </button>
              </div>
              <div className="flex justify-between w-full mt-2 mb-1 px-2">
                <div className="flex flex-col items-center">
                  <span className="text-xs text-gray-500 mb-1">Casual Reader</span>
                  <span className="font-semibold text-gray-700 text-base">6 books</span>
                </div>
                <div className="flex flex-col items-center justify-end">
                  <span className="font-semibold text-gray-700 text-base">12 books</span>
                </div>
                <div className="flex flex-col items-center justify-end">
                  <span className="font-semibold text-gray-700 text-base">24 books</span>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-xs text-gray-500 mb-1">Bookworm</span>
                  <span className="font-semibold text-gray-700 text-base">52 books</span>
                </div>
              </div>
            </div>
            {error && (
              <div className="text-red-600 text-sm mb-4 mt-2">{error}</div>
            )}
            <button
              onClick={handleComplete}
              className="w-full py-3 rounded-full bg-[#2d2d2d] text-white font-semibold text-lg shadow-sm hover:bg-[#222] transition disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              Complete Setup
            </button>
            <p className="mt-3 text-sm text-gray-400 text-center">
              You can adjust your goals anytime in settings
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadingGoals; 